import { ShieldCheck, Eye, Wrench, Users, TrendingUp, Radar } from "lucide-react";
import { PageHero } from "@/components/ui/PageHero";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { pageSeo } from "@/config/seo";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const seo = pageSeo(
  "About Us — Building the Infrastructure Behind Digital Growth",
  "The Planet helps businesses establish, operate and scale their digital infrastructure through server, cloud, email and network solutions."
);

const values = [
  { icon: ShieldCheck, title: "Reliability", body: "Infrastructure and decisions our customers can depend on." },
  { icon: Eye, title: "Clarity", body: "Straightforward information, without overselling what a solution can do." },
  { icon: Wrench, title: "Technical Responsibility", body: "Recommending what a workload actually needs, not what's easiest to sell." },
  { icon: Users, title: "Customer-First Thinking", body: "Starting from what the business is trying to achieve, not the product catalogue." },
  { icon: TrendingUp, title: "Continuous Improvement", body: "Revisiting how we work as infrastructure and business needs evolve." },
  { icon: Radar, title: "Security Awareness", body: "Treating security as part of every infrastructure decision, not an add-on." },
];

export function AboutPage() {
  useDocumentMeta(seo.title, seo.description);

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Building the Infrastructure Behind Digital Growth"
        description="The Planet helps businesses establish, operate and scale their digital infrastructure through server, cloud, email and network solutions."
      />

      <section className="mx-auto max-w-3xl px-5 py-20 lg:px-8">
        <p className="text-base leading-relaxed text-text-secondary">
          The Planet helps businesses establish, operate and scale their digital infrastructure
          through server, cloud, email and network solutions.
        </p>
        <p className="mt-5 text-base leading-relaxed text-text-secondary">
          Our goal is to make essential technology infrastructure easier to understand, select
          and manage.
        </p>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="rounded-card border border-border bg-navy-card p-7">
            <SectionEyebrow>Mission</SectionEyebrow>
            <p className="mt-4 text-sm leading-relaxed text-text-secondary">
              To provide practical, scalable and performance-focused digital infrastructure
              solutions for organisations at every stage of growth.
            </p>
          </div>
          <div className="rounded-card border border-border bg-navy-card p-7">
            <SectionEyebrow>Vision</SectionEyebrow>
            <p className="mt-4 text-sm leading-relaxed text-text-secondary">
              To become a trusted infrastructure partner for businesses building and expanding
              their digital operations.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-navy-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <SectionEyebrow>What We Stand For</SectionEyebrow>
            <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
              Our Values
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((v) => (
              <div key={v.title} className="rounded-card border border-border bg-navy-card p-6">
                <v.icon className="h-5 w-5 text-cyan" strokeWidth={1.6} />
                <h3 className="mt-4 font-display text-base font-semibold text-text-primary">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
